import JWTService from '@/services/JWTService';
import { apiService } from '@/services/APIService';
import { rServices } from '@/plugins/api';
import Vue from 'vue';
import UserAttribute from '@/models/list/user-attribute';
import { checkRole } from '@/helpers/role';
import { ROLES, ROLES_NAMES, ROLES_DATA } from '@/constants/roles';
import { DIRECTORY_AGE_ATTRIBUTES_IDS } from '@/modules/UserProfile/components/Profile/Sections/constants';

export default {
  name: 'auth',
  namespaced: true,
  state: {
    refreshToken: '',
    userData: {},
    authenticated: false,
    isTokenLoading: false,

    whitelistVchtOrganizations: [],

    visitedEvents: 0,
    /**
     *  {
     *    id: number;
     *    directory_member_type: number;
     *    directory_id: number;
     *    values: number[];
     *  }[];
     */
    userAttributes: [],
    /**
     *  number[];
     */
    hideWelcomeDialogFor: [],
    // currently not used
    /**
     *  number[];
     */
    // hideConfirmSkipVoteDialogFor: [],
    /**
     *  number[];
     */
    hideUserDuplicateDialogFor: [],

    hasEventAssociations: false,
  },
  getters: {
    isAuthenticated(state) {
      return state.authenticated;
    },
    getRefreshToken(state) {
      return state.refreshToken;
    },
    getUserData(state) {
      return state.userData;
    },
    getUserRolesNew(state) {
      return (state.userData.roles || [ROLES_DATA.guest]).map((role) => ({
        ...role,
        label: ROLES_NAMES[role.name],
      }));
    },
    getUserRoles(state) {
      // DEPRECATED
      const roles = (state.userData?.roles || []).map(({ name }) => name);
      return Array.from(new Set(roles));
    },
    isTokenLoading(state) {
      return state.isTokenLoading;
    },
    isCertificate(_, getters) {
      return checkRole(getters.getUserRoles, ROLES.certificate);
    },
    isMember(_, getters) {
      return checkRole(getters.getUserRoles, ROLES.member);
    },
    isOperator(_, getters) {
      return checkRole(getters.getUserRoles, ROLES.operator);
    },
    isParticipant(_, getters) {
      return getters.isCertificate || getters.isMember;
    },
    isOrganization(_, getters) {
      return checkRole(getters.getUserRoles, ROLES.organization);
    },
    isAdmin(_, getters) {
      return checkRole(getters.getUserRoles, ROLES.admin);
    },
    isExpert(_, getters) {
      return checkRole(getters.getUserRoles, ROLES.expert);
    },
    isTrainer(_, getters) {
      return checkRole(getters.getUserRoles, ROLES.trainer);
    },
    isEventManager(_, getters) {
      return checkRole(getters.getUserRoles, ROLES.event_manager);
    },
    isDepartment(_, getters) {
      return checkRole(getters.getUserRoles, ROLES.department);
    },
    isModerator(_, getters) {
      return checkRole(getters.getUserRoles, ROLES.moderator);
    },

    userRegionId(_, getters) {
      const user = getters.getUserData;
      let regionId = null;

      if (checkRole(getters.getUserRoles, [ROLES.operator, ROLES.certificate, ROLES.member])) {
        regionId = user.profile?.region.id;
      }

      if (checkRole(getters.getUserRoles, ROLES.department)) {
        regionId = user?.department.region_id;
      }

      if (checkRole(getters.getUserRoles, ROLES.organization)) {
        regionId = user.organizer?.region?.id;
      }

      return regionId || user?.region?.id || user?.region || null;
    },

    userRegionName(_, getters) {
      const user = getters.getUserData;
      let regionId = null;

      if (checkRole(getters.getUserRoles, [ROLES.operator, ROLES.certificate, ROLES.member])) {
        regionId = user.profile?.region.name;
      }

      if (checkRole(getters.getUserRoles, ROLES.department)) {
        regionId = user?.department.region_name;
      }

      if (checkRole(getters.getUserRoles, ROLES.organization)) {
        regionId = user.organizer?.region?.name;
      }

      return regionId || user?.region?.name || user?.region || null;
    },

    getUserMunicipality(_, getters) {
      const user = getters.getUserData;
      let municipality = null;

      if (checkRole(getters.getUserRoles, [ROLES.operator, ROLES.certificate, ROLES.member])) {
        municipality = user.profile?.municipality;
      }

      if (checkRole(getters.getUserRoles, ROLES.organization)) {
        municipality = user.organizer?.municipality;
      }

      if (checkRole(getters.getUserRoles, ROLES.department)) {
        municipality = user.department?.id;
      }

      return municipality;
    },

    userMunicipalityId(_, getters) {
      const user = getters.getUserData;
      const municipality = getters.getUserMunicipality;

      return municipality?.id || user?.municipality?.id || user?.municipality || municipality || null;
    },

    userMunicipalityName(_, getters) {
      const user = getters.getUserData;
      const municipality = getters.getUserMunicipality;

      return municipality?.name || user?.municipality?.name || user?.municipality || null;
    },
    whitelistVchtOrganizations(state) {
      return state.whitelistVchtOrganizations;
    },
    getVisitedEvents: (state) => state.visitedEvents,
    getUserAttributes: (state) => state.userAttributes,
    getFilteredUserAttributes: (state) =>
      state.userAttributes.filter(({ directory_id }) => !DIRECTORY_AGE_ATTRIBUTES_IDS.includes(directory_id)),
    getAgeUserAttribute: (state) => state.userAttributes.find(({ directory_id }) => DIRECTORY_AGE_ATTRIBUTES_IDS.includes(directory_id)),
    getHideWelcomeDialogFor: (state) => state.hideWelcomeDialogFor,
    // currently not used
    // getHideConfirmSkipVoteDialogFor: (state) => state.hideConfirmSkipVoteDialogFor,
    getHideUserDuplicateDialogFor: (state) => state.hideUserDuplicateDialogFor,

    hasEventAssociations: (state) => state.hasEventAssociations,
  },

  actions: {
    async init({ commit, dispatch, getters }) {
      // // - @ЗАГЛУШКА
      // return;
      // /* eslint-disable no-unreachable */
      const token = JWTService.getToken();
      if (!token) {
        return;
      }

      commit('SET_TOKEN_LOADING', true);
      apiService.mountAuthInterceptors();

      const params = {
        expand: 'profile,organizer',
      };

      const { response, error } = await rServices.user.getUser({ params }).finally(() => {
        commit('SET_TOKEN_LOADING', false);
      });

      if (error) {
        commit('LOGOUT_USER', {});
        JWTService.clearTokens();
        return;
      }

      const userData = response.data.data;
      commit('SET_USER', userData);
      if (checkRole(getters.getUserRoles, [ROLES.certificate, ROLES.member])) {
        dispatch('getUserAttributes', userData.id);
      }
      if (getters.isOrganization) {
        await dispatch('checkAssocitions');
      }
      if (getters.isEventManager) {
        /**
         * Берём первого организатора,
         * потому что пока что интерфейс подразумевает только одного организатора
         */
        const [organizer] = userData.manager_organizers || [];
        if (organizer) {
          dispatch('setEventManagerOrganizer', organizer);
        }
        dispatch('setEventManagerPermissions', userData.manager_permissions);
      }
      dispatch('notification/connect', null, { root: true });
      if ((userData.region || userData.profile?.region) && !getters.isEventManager && !getters.isTrainer && !getters.isModerator) {
        dispatch('fetchRegionSettings', null, { root: true });
      }
      /** В перспективе вырезать обе системы снизу, они уже неактуальны */
      // dispatch('fetchWhitelistVchtOrganizations');
    },
    async fetchWhitelistVchtOrganizations({ commit }) {
      const { response, error } = await rServices.directories.whitelistVchtOrganizations();
      if (error) {
        return;
      }

      commit('SET_VCHT_WHITE_LIST', response.data.data);
    },
    setAuthData({ dispatch }, authData) {
      JWTService.setToken(authData.access_token);
      JWTService.setRefreshToken(authData.refresh_token);

      return dispatch('init');
    },
    patchUser({ commit, getters }, data) {
      commit('SET_USER', { ...getters.getUserData, ...data });
      commit('CLEAR_SETTINGS', null, { root: true });
    },
    async authWithPFDO({ dispatch, commit }, payload) {
      commit('SET_TOKEN_LOADING', true);

      const { response, error } = await rServices.auth.authWithPFDO({ data: payload }).finally(() => {
        commit('SET_TOKEN_LOADING', false);
      });

      if (error) {
        return;
      }

      JWTService.setToken(response.data.data.access_token, '');
      dispatch('init');
    },

    logout({ commit }) {
      JWTService.clearTokens();
      commit('LOGOUT_USER');
      commit('CLEAR_SETTINGS', null, { root: true });
    },

    setRefreshToken({ commit }, token) {
      commit('SET_REFRESH_TOKEN', token);
    },

    // DEPRECATED
    setUserTimezone({ commit }, timezone) {
      commit('SET_TIMEZONE', timezone);
    },

    async reloadUser({ commit }) {
      const params = {
        expand: 'profile,organizer',
      };

      const { response, error } = await rServices.user.getUser({ params });
      if (error) {
        return;
      }

      const userData = response.data.data;
      commit('SET_USER', userData);
    },
    visitEvent({ commit }) {
      commit('ADD_VISITED_EVENTS');
    },
    resetVisitedEvents({ commit }) {
      commit('RESET_VISITED_EVENTS');
    },
    async getUserAttributes({ commit }, userId) {
      const params = {
        'search[user_id]': userId,
      };

      const { response, error } = await rServices.user.getUserAttributes({ params });
      if (error) {
        return;
      }

      const userAttributes = response.data.data;
      if (!userAttributes.length) {
        return;
      }

      const attributes = userAttributes.map((attribute) => new UserAttribute(attribute));
      commit('SET_USER_ATTRIBUTES', attributes);
    },
    setUserAttributes({ commit }, userAttributes) {
      const attributes = userAttributes.map((attribute) => new UserAttribute(attribute));
      commit('SET_USER_ATTRIBUTES', attributes);
    },
    async checkAssocitions({ commit }) {
      const params = {
        'per-page': 1,
        'search[my]': 1,
      };
      const { response } = await rServices.eventAssociations.getAll({ params });
      commit('SET_HAS_EVENT_ASSOCIATIONS', !!(response?.data?.data || []).length);
    },
    async setEventManagerOrganizer({ commit }, organizer) {
      if (!organizer?.pivot.is_active) {
        const payload = {
          organizer_id: organizer.id,
        };

        const { error } = await rServices.eventManagers.setActiveOrganizer({ data: payload });
        if (error) {
          return;
        }
      }
      commit('SET_EVENT_MANAGER_ORGANIZER', organizer);
    },
    setEventManagerPermissions({ commit }, permissions) {
      commit('SET_PERMISSIONS', permissions || []);
    },
    hideWelcomeDialogForUser({ commit }, userId) {
      commit('HIDE_WELCOME_DIALOG_FOR_USER', userId);
    },
    // currently not used
    // hideConfirmSkipVoteDialogForUser({ commit }, userId) {
    //   commit('HIDE_CONFIRM_SKIP_VOTE_DIALOG_FOR_USER', userId);
    // },
    hideUserDuplicateDialogForUser({ commit }, userId) {
      commit('HIDE_USER_DUPLICATE_DIALOG_FOR_USER', userId);
    },
  },
  mutations: {
    SET_USER(state, data) {
      state.userData = data;
      state.authenticated = true;
    },
    SET_REFRESH_TOKEN(state, token) {
      state.refreshToken = token;
    },
    SET_TOKEN_LOADING(state, value) {
      state.isTokenLoading = value;
    },
    LOGOUT_USER(state) {
      state.userData = {};
      state.authenticated = false;
      state.userAttributes = [];
      state.hasEventAssociations = false;
      JWTService.clearTokens();
    },
    SET_TIMEZONE(state, timezone) {
      state.userData.timezone = timezone;
    },
    SET_VCHT_WHITE_LIST(state, payload) {
      state.whitelistVchtOrganizations = payload;
    },
    ADD_VISITED_EVENTS(state) {
      state.visitedEvents += 1;
    },
    RESET_VISITED_EVENTS(state) {
      state.visitedEvents = 0;
    },
    SET_TIMEZONES(state, data) {
      state.timezones = data;
    },
    SET_USER_ATTRIBUTES(state, data) {
      state.userAttributes = data;
    },
    SET_HAS_EVENT_ASSOCIATIONS(state, flag) {
      state.hasEventAssociations = flag;
    },
    SET_EVENT_MANAGER_ORGANIZER(state, data) {
      state.userData.organizer = data;
    },
    SET_PERMISSIONS(state, data) {
      state.userData.permissions = data;
    },
    HIDE_WELCOME_DIALOG_FOR_USER(state, userId) {
      if (!state.hideWelcomeDialogFor.includes(userId)) {
        state.hideWelcomeDialogFor.push(userId);

        if (state.hideWelcomeDialogFor.length > 10) {
          state.hideWelcomeDialogFor.splice(0, 1);
        }
      }
    },
    // currently not used
    // HIDE_CONFIRM_SKIP_VOTE_DIALOG_FOR_USER(state, userId) {
    //   if (!state.hideConfirmSkipVoteDialogFor.includes(userId)) {
    //     state.hideConfirmSkipVoteDialogFor.push(userId);

    //     if (state.hideConfirmVoteDialogFor.length > 10) {
    //       state.hideConfirmSkipVoteDialogFor.splice(0, 1);
    //     }
    //   }
    // },
    HIDE_USER_DUPLICATE_DIALOG_FOR_USER(state, userId) {
      if (!state.hideUserDuplicateDialogFor.includes(userId)) {
        state.hideUserDuplicateDialogFor.push(userId);

        if (state.hideUserDuplicateDialogFor.length > 10) {
          state.hideUserDuplicateDialogFor.splice(0, 1);
        }
      }
    },
  },
};
